import React from 'react';
import { FaTriangleExclamation } from 'react-icons/fa6';
import Badge from '@/components/Badges';
import { executionPathMeta } from './constants';

/**
 * The headline answer for a store: which path the CDC pipeline will take on its
 * next file, why, and what is wrong with the configuration.
 *
 * This matters more than it looks. A store can have a full chain of imported
 * Glue steps and still run the lossy mapping-only path, because disabling any
 * single step makes the pipeline skip the whole chain. `execution_path` is the
 * only field that tells you which one actually runs.
 */
const ExecutionPathBanner = ({ path, reason, warnings = [], multiFile = false }) => {
  const meta = executionPathMeta(path, { multiFile });
  const isLossy = path !== 'steps';
  // A multi-file store on the mapping path is staging the wrong dataset, not a
  // degraded one, so it gets the harsher treatment.
  const isWrongDataset = isLossy && multiFile;

  const tone = isWrongDataset
    ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800'
    : isLossy
      ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
      : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800';

  return (
    <div className={`border rounded-lg p-4 ${tone}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          Execution path
        </span>
        <Badge color={meta.color} size="xs">
          {meta.label}
        </Badge>
        {multiFile && (
          <Badge color="blue" size="xs">
            Multi-file feed
          </Badge>
        )}
      </div>

      {reason && (
        <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{reason}</p>
      )}
      {meta.blurb && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{meta.blurb}</p>
      )}

      {warnings.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {warnings.map((warning, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-200"
            >
              <FaTriangleExclamation className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{warning}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ExecutionPathBanner;
